import React, { useState, useEffect, useRef, useCallback } from "react";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";

import { useTranslation } from "react-i18next";

import { useDebounce } from "../../../../hooks/useDebounce";
import useCRUDService from "../../../../hooks/useCRUDService";
import useApi from "../../../../hooks/useApi";

import Layout from "../../../../layout/layout";
import Header from "../../../../layout/header";
import useSkeleton from "../../../../hooks/useSkeleton";

import helperFormats from "../../../../utils/helperFormats";

import { useAtom } from "jotai";
import { loadingState } from "../../../../states/loadingState";
import { toastState } from "../../../../states/toastState";

const Installments = () => {
  const [isLoading, setIsLoading] = useAtom(loadingState);
  const [toastConfig, setToastConfig] = useAtom(toastState);

  const s = useSkeleton("commons");
  const api = useApi();
  const { t } = useTranslation();
  const CRUDService = useCRUDService("installments");

  const [entities, setEntities] = useState(null);

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(100);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [globalFilter, setGlobalFilter] = useState("");

  const [totalRecords, setTotalRecords] = useState(0);

  const searchInput = useRef(null);

  // Inicializamos el debounce para la búsqueda
  const debouncedSearchTerm = useDebounce(globalFilter, 2000);

  const toast = useRef(null);
  const dt = useRef(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const columns = JSON.stringify([
        {
          name: "credit.application.beneficiary_DocumentNumber",
          type: "string",
        },
        {
          name: "credit.code",
          type: "string",
        },
        {
          name: "number",
          type: "number",
        },
        {
          name: "capital",
          type: "decimal",
        },
        {
          name: "interests",
          type: "decimal",
        },
        {
          name: "interestsTaxes",
          type: "decimal",
        },
        {
          name: "expirationDate",
          type: "date",
        },
        {
          name: "status",
          type: "string",
        },
        {
          name: "statusDate",
          type: "date",
        },
      ]);

      const response = await CRUDService.getEntities(
        columns,
        first,
        rows,
        sortField ?? "expirationDate",
        sortOrder >= 0 ? "ASC" : "DESC",
        debouncedSearchTerm
      );

      console.log(response);
      setTotalRecords(response.data.totalRows);
      setEntities(response.data.rows);
    } catch (error) {
      setToastConfig({
        severity: "error",
        summary: "Error",
        detail: t("errors.general"),
        life: 3000,
        visible: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [first, rows, sortField, sortOrder, debouncedSearchTerm, CRUDService]);

  useEffect(() => {
    fetchData();
  }, [first, rows, sortField, sortOrder, debouncedSearchTerm]);

  const exportCSV = () => {
    dt.current.exportCSV();
  };

  const header = (
    <div className="table-header">
      <span className="p-input-icon-left">
        <i className="pi pi-search" />
        <InputText
          ref={searchInput}
          type="search"
          onInput={(e) => setGlobalFilter(e.target.value)}
          placeholder={`${t("DataTable.Search")}...`}
        />
      </span>
      <Button
        outlined
        label={t("DataTable.Export")}
        icon="pi pi-upload"
        className="p-button-secondary"
        onClick={exportCSV}
        style={{
          marginLeft: "20px",
        }}
      />
    </div>
  );

  return (
    <Layout
      header={
        <Header
          icon={<i className="pi pi-calendar"></i>}
          title={t("entities.installments.plural")}
        />
      }
    >
      <Toast ref={toast} />
      <div className={s.get("dataTableContainer")}>
        <DataTable
          onPage={(e) => {
            setFirst(e.first);
            setRows(e.rows);
          }}
          onSort={(e) => {
            setSortField(e.sortField);
            setSortOrder(e.sortOrder);
          }}
          sortField={sortField}
          sortOrder={sortOrder}
          ref={dt}
          value={entities}
          lazy
          paginator
          first={first}
          rows={rows}
          totalRecords={totalRecords}
          columnResizeMode="fit"
          showGridlines
          dataKey="id"
          rowsPerPageOptions={[10, 25, 100, 1000]}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate={`${t(
            "DataTable.currentPageReportTemplate"
          )} ${t("Installments")}`}
          globalFilter={globalFilter}
          header={header}
          className="flex flex-column w-full"
        >
          <Column
            field="credit.application.beneficiary_DocumentNumber"
            bodyStyle={{ textAlign: "center" }}
            header={t("entities.installments.beneficiary_DocumentNumber")}
            sortable
          ></Column>
          <Column
            field="credit.code"
            bodyStyle={{ textAlign: "center" }}
            header={t("entities.installments.creditCode")}
            sortable
          ></Column>
          <Column
            field="number"
            bodyStyle={{ textAlign: "center" }}
            header={t("entities.installments.number")}
            sortable
          ></Column>
          <Column
            field="capital"
            header={t("entities.installments.capital")}
            bodyStyle={{ textAlign: "right" }}
            body={(rowData) => {
              return helperFormats.formatDecimal(rowData.capital);
            }}
            sortable
          ></Column>
          <Column
            field="interests"
            header={t("entities.installments.interests")}
            bodyStyle={{ textAlign: "right" }}
            body={(rowData) => {
              return helperFormats.formatDecimal(rowData.interests);
            }}
            sortable
          ></Column>
          <Column
            field="interestsTaxes"
            header={t("entities.installments.interestsTaxes")}
            bodyStyle={{ textAlign: "right" }}
            body={(rowData) => {
              return helperFormats.formatDecimal(rowData.interestsTaxes);
            }}
            sortable
          ></Column>
          <Column
            field="expirationDate"
            header={t("entities.installments.expirationDate")}
            bodyStyle={{ textAlign: "center" }}
            body={(rowData) =>
              helperFormats.formatDate(new Date(rowData.expirationDate))
            }
            sortable
          ></Column>
          <Column
            field="status"
            header={t("entities.installments.status")}
            bodyStyle={{ textAlign: "center" }}
            sortable
          ></Column>
          <Column
            field="statusDate"
            header={t("entities.installments.statusDate")}
            bodyStyle={{ textAlign: "center" }}
            body={(rowData) =>
              helperFormats.formatDate(new Date(rowData.statusDate))
            }
            sortable
          ></Column>
        </DataTable>
      </div>
    </Layout>
  );
};

export default Installments;
